import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import type { Order } from '../types/@types/order';
import { getTakeawayOrders, payOrder } from '../api/orders';
import { echo } from '../lib/echo';

export const TAKEAWAY_ORDERS_QUERY_KEY = [
  'orders',
  'takeaway',
  { paid: false },
] as const;

interface PayOrderContext {
  previousOrders?: Order[];
}

function sortTakeawayOrders(orders: Order[]): Order[] {
  return [...orders].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;

    if (Number.isFinite(aTime) && Number.isFinite(bTime)) {
      return bTime - aTime;
    }

    if (!Number.isFinite(aTime) && Number.isFinite(bTime)) {
      return 1;
    }

    if (Number.isFinite(aTime) && !Number.isFinite(bTime)) {
      return -1;
    }

    return 0;
  });
}

export function useTakeawayOrders() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const upsertOrder = (incoming: Order) => {
      if (!incoming?.id) return;
      queryClient.setQueryData<Order[] | undefined>(
        TAKEAWAY_ORDERS_QUERY_KEY,
        (current = []) => {
          const filtered = current.filter((order) => order.id !== incoming.id);
          return sortTakeawayOrders([...filtered, incoming]);
        },
      );
    };

    const removeOrder = (orderId?: string | number) => {
      if (!orderId) return;
      queryClient.setQueryData<Order[] | undefined>(
        TAKEAWAY_ORDERS_QUERY_KEY,
        (current = []) => current.filter((order) => String(order.id) !== String(orderId)),
      );
    };

    const handleCreated = (payload: { order: Order }) => {
      const order = payload?.order;
      if (order?.destination !== 'TAKEAWAY') return;
      if (order.paid && order.status === 'DELIVERED') return;
      upsertOrder(order);
    };

    const handleUpdated = (payload: { order: Order }) => {
      const order = payload?.order;
      if (order?.destination !== 'TAKEAWAY') return;
      if (order.paid && order.status === 'DELIVERED') {
        removeOrder(order.id);
      } else {
        upsertOrder(order);
      }
    };

    const channel = echo.channel('orders');
    channel.listen('.orders.new', handleCreated);
    channel.listen('.orders.updated', handleUpdated);

    return () => {
      if (channel && typeof channel.stopListening === 'function') {
        channel.stopListening('.orders.new');
        channel.stopListening('.orders.updated');
      }
      if (echo && typeof echo.leave === 'function') {
        echo.leave('orders');
      }
    };
  }, [queryClient]);

  return useQuery<Order[]>({
    queryKey: TAKEAWAY_ORDERS_QUERY_KEY,
    queryFn: () => getTakeawayOrders({ paid: false }),
    refetchOnWindowFocus: true,
  });
}

export function usePayOrder() {
  const queryClient = useQueryClient();

  return useMutation<Order, AxiosError<{ message?: string }>, string, PayOrderContext>({
    mutationFn: (orderId: string) => payOrder(orderId),
    onMutate: async (orderId: string) => {
      await queryClient.cancelQueries({ queryKey: TAKEAWAY_ORDERS_QUERY_KEY });
      const previousOrders = queryClient.getQueryData<Order[]>(
        TAKEAWAY_ORDERS_QUERY_KEY,
      );

      queryClient.setQueryData<Order[] | undefined>(
        TAKEAWAY_ORDERS_QUERY_KEY,
        (current = []) => current.filter((order) => String(order.id) !== orderId),
      );

      return { previousOrders };
    },
    onSuccess: () => {
      toast.success('Pedido pagado');
    },
    onError: (error, _variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData(TAKEAWAY_ORDERS_QUERY_KEY, context.previousOrders);
      }

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('No se pudo cobrar el pedido');
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TAKEAWAY_ORDERS_QUERY_KEY });
    },
  });
}
